import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { Briefcase, FlaskConical, UserCircle, ArrowRight, AlertTriangle, CheckCircle2, FileText } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const tiles = [
    { to: "/jobs", icon: Briefcase, title: "Jobs & Internships", desc: "Browse & apply to curated roles", color: "#7A222B" },
    { to: "/projects", icon: FlaskConical, title: "AI Projects", desc: "Get paid, build proof of work", color: "#2E7D32" },
    { to: "/profile", icon: UserCircle, title: "My Profile", desc: "Keep your profile AI-ready", color: "#6E665E" },
  ];

  return (
    <PageShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">Dashboard</span>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
            Hi {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
        </div>
      </div>

      {profile && !profile.profile_complete && (
        <div data-testid="profile-incomplete-banner" className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#FDF0EC] border border-[#F8D7C4] rounded-2xl px-6 py-5">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[#7A222B] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#2D2825]">Complete your profile to apply for jobs</p>
              <p className="text-sm text-[#6E665E]">Add a headline, college and at least 3 skills to unlock applications.</p>
            </div>
          </div>
          <Link to="/profile" data-testid="complete-profile-btn" className="shrink-0 bg-[#7A222B] hover:bg-[#5C1D24] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors">
            Complete profile
          </Link>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} data-testid={`dash-tile-${t.title.toLowerCase().replace(/\s/g, "-")}`} className="group bg-white rounded-3xl border border-[#EAE3DB] p-7 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${t.color}1A` }}>
              <t.icon className="w-6 h-6" style={{ color: t.color }} />
            </div>
            <h3 className="mt-5 text-lg font-semibold text-[#2D2825]">{t.title}</h3>
            <p className="mt-1 text-sm text-[#6E665E]">{t.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#7A222B] group-hover:gap-2 transition-all">
              Open <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-display text-2xl font-bold text-[#2D2825]" style={{ fontWeight: 700 }}>My applications</h2>
        {apps.length === 0 ? (
          <div data-testid="no-applications" className="mt-5 bg-white border border-[#EAE3DB] rounded-2xl p-10 text-center">
            <FileText className="w-10 h-10 text-[#948A80] mx-auto" />
            <p className="mt-4 text-[#6E665E]">No applications yet. Browse the AI job board to get started.</p>
            <Link to="/jobs" className="mt-5 inline-flex items-center gap-2 bg-[#7A222B] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#5C1D24] transition-colors">Explore jobs</Link>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {apps.map((a) => (
              <div key={a.id} data-testid={`application-${a.id}`} className="bg-white border border-[#EAE3DB] rounded-2xl px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#2D2825]">{a.job_title}</p>
                  <p className="text-sm text-[#6E665E]">{a.company}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted: { label: "Submitted", cls: "bg-[#F5EFE6] text-[#6E665E]", icon: CheckCircle2 },
    shortlisted: { label: "Shortlisted", cls: "bg-[#E6F4EA] text-[#2E7D32]", icon: CheckCircle2 },
    rejected: { label: "Not selected", cls: "bg-[#FAF0F1] text-[#7A222B]", icon: AlertTriangle },
  };
  const s = map[status] || map.submitted;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${s.cls}`}>
      <s.icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}
